import React, { useState, useEffect, useRef } from 'react'
import './ProjectsPage.css'
import { useSelector, useDispatch } from "react-redux"
import MenuBar from "../components/MenuBar"
import Header from "../components/Header"
import { useNavigate, useParams } from "react-router-dom"
import LoadingSpinner from '../components/placeholders/LoadingSpinner'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import httpClient from '../components/helpers/httpClient'
import Label from '../components/Label'
import DropDownFilter from '../components/DropDownFilter'
import { updateFilters } from '../store/projectsPage'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import CloseIcon from '@mui/icons-material/Close'
import Dropdown from '../components/Dropdown'
import { RxDragHandleHorizontal } from "react-icons/rx"
import { toast } from "react-toastify"
import ExportButton from '../components/UI/ExportButton.jsx'
import Button from '../components/UI/Button.jsx'
import Slider from '../components/Slider'
import { EXPORT_LIMIT, flattenExportRows } from '../components/helpers/exportUtils'

function ProjectsPage() {
  const [projects, setProjects] = useState([])
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [totalPages, setTotalPages] = useState(0)
  const [limit, setLimit] = useState(25)
  const [createNew, setCreateNew] = useState(false)
  const [newProjectStatus, setNewProjectStatus] = useState("")
  const [statuses, setStatuses] = useState([])
  const [tagsList, setTagsList] = useState([])
  
  // Website order modal state
  const [websiteOrderModalOpen, setWebsiteOrderModalOpen] = useState(false)
  const [publishedProjects, setPublishedProjects] = useState([])
  const [loadingPublishedProjects, setLoadingPublishedProjects] = useState(false)
  const [savingOrder, setSavingOrder] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState(null)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const saveButton = useRef(null)
  const filters = useSelector(state => state.projectsPage.filters)

  const fetchTagsList = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + "/api/tags/get")
    const data = response.data
    const transformedData = data.map(tag => {
      return (
        {
          "id": tag.id,
          "name": tag.tag
        }
      )
    })

    setTagsList(transformedData)
  }

  const fetchProjectStatuses = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/projects/statuses`)
    const data = response.data
    const transformedData = data.map(status => {
      return (
        { "id": status.id, "name": status.status_name }
      )
    })
    setStatuses(transformedData)
  }

  const fetchProjects = async () => {
    setLoadingProjects(true)
    const response = await httpClient.post(import.meta.env.VITE_API_URL + `/api/projects/get?page=${filters.page}&limit=${limit}`, filters)
    setProjects(response.data.projects)
    setTotalPages(response.data.total_pages)
    setLoadingProjects(false)
  }

  const prepareExportData = async () => {
    const response = await httpClient.post(
      import.meta.env.VITE_API_URL + `/api/projects/get?page=1&limit=${EXPORT_LIMIT}`,
      filters
    )

    return flattenExportRows(response.data?.projects || [])
  }

  useEffect(() => {
    fetchProjects()
    fetchProjectStatuses()
    fetchTagsList()
  }, [])

  useEffect(() => {
    fetchProjects()
  }, [filters])

  const handleOnClickRow = (projectID) => {
    navigate(`/projects/${projectID}`)
  }

  const handlePagination = (event) => {
    const element = event.currentTarget

    if (element.classList.contains("grayed-out")) {
      return
    }

    const type = element.dataset.type

    const updatedFilters = { ...filters }

    switch (type) {

      case "previous":
        updatedFilters["page"] = filters.page - 1

        dispatch(updateFilters(updatedFilters))
        break
      case "next":
        updatedFilters["page"] = filters.page + 1

        dispatch(updateFilters(updatedFilters))
        break
    }
  }

  const handleOnClickDropdownItem = (e, selectedItem = null, idList) => {
    let value = e.currentTarget.textContent

    const newFiltersObj = { ...filters }

    switch (idList) {
      case 'projects_filter--status':
        if (selectedItem && typeof selectedItem === 'object') {
          value = selectedItem
        } else if (selectedItem) {
          const status = statuses.find(status => status.id == selectedItem)
          value = status || { id: null, name: "" }
        }
        newFiltersObj.statusFilter = value
        break
      case 'projects_filter--tag':
        if (selectedItem && typeof selectedItem === 'object') {
          value = selectedItem
        } else if (selectedItem) {
          const tag = tagsList.find(tag => tag.id == selectedItem)
          value = tag || { id: null, name: "" }
        }
        newFiltersObj.tagFilter = value
        break
    }

    newFiltersObj.page = 1
    dispatch(updateFilters(newFiltersObj))
  }

  const handleSearchWord = (event) => {
    const updatedFilters = { ...filters }
    updatedFilters["searchWord"] = event.target.value
    updatedFilters["page"] = 1

    dispatch(updateFilters(updatedFilters))
  }

  const handleClearSearchBar = () => {
    if (filters.searchWord == "") {
      return
    } else {
      const updatedFilters = { ...filters }
      updatedFilters["searchWord"] = ""
      updatedFilters["page"] = 1

      dispatch(updateFilters(updatedFilters))
    }
  }

  const handleSetSortFilter = (event) => {

    const innerHTML = event.currentTarget.innerHTML
    const updatedFilters = { ...filters }

    switch (innerHTML) {
      case "id":
        if (filters.sortFilter == "id") {
          updatedFilters["sortAscending"] = !filters.sortAscending
        } else {
          updatedFilters["sortFilter"] = "id"
          updatedFilters["sortAscending"] = true
        }
        break
      case "Working name":
        if (filters.sortFilter == "project_placeholder_name") {
          updatedFilters["sortAscending"] = !filters.sortAscending
        } else {
          updatedFilters["sortFilter"] = "project_placeholder_name"
          updatedFilters["sortAscending"] = true
        }
        break
      case "Commercial name":
        if (filters.sortFilter == "project_commercial_name") {
          updatedFilters["sortAscending"] = !filters.sortAscending
        } else {
          updatedFilters["sortFilter"] = "project_commercial_name"
          updatedFilters["sortAscending"] = true
        }
        break
      case "Status":
        if (filters.sortFilter == "status") {
          updatedFilters["sortAscending"] = !filters.sortAscending
        } else {
          updatedFilters["sortFilter"] = "status"
          updatedFilters["sortAscending"] = true
        }
        break

    }
    updatedFilters["page"] = 1
    dispatch(updateFilters(updatedFilters))
  }

  const handleSaveNewProject = async () => {
    saveButton.disabled = true

    const newProjectInfo = {}
    const inputElements = document.querySelectorAll(".input_field__input_box--text")

    inputElements.forEach(element => {
      const key = element.getAttribute('name')
      newProjectInfo[key] = element.value
    })

    const data = {
      newProjectStatus,
      newProjectInfo
    }

    const response = await httpClient.post(import.meta.env.VITE_API_URL + `/api/projects/create`, data)
    console.log(response.data)

    fetchProjects()

    // cleanup
    setNewProjectStatus("")
    setCreateNew(false)

    inputElements.forEach(element => {
      element.value = ""
    })

    saveButton.disabled = false
  }

  // Website Order Modal Functions
  const fetchPublishedProjects = async () => {
    setLoadingPublishedProjects(true)
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/projects/website-order`)
      setPublishedProjects(response.data)
    } catch (error) {
      console.error("Error fetching published projects:", error)
      toast.error("Failed to load published projects")
    }
    setLoadingPublishedProjects(false)
  }

  const handleOpenWebsiteOrderModal = () => {
    setWebsiteOrderModalOpen(true)
    fetchPublishedProjects()
  }

  const handleCloseWebsiteOrderModal = () => {
    setWebsiteOrderModalOpen(false)
    setPublishedProjects([])
  }

  const handleDragStart = (e, index) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', e.target.outerHTML)
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    
    if (draggedIndex === null || draggedIndex === index) return
    
    // Reorder the array
    const newProjects = [...publishedProjects]
    const draggedProject = newProjects[draggedIndex]
    newProjects.splice(draggedIndex, 1)
    newProjects.splice(index, 0, draggedProject)
    
    setPublishedProjects(newProjects)
    setDraggedIndex(index)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
  }

  const handleSaveWebsiteOrder = async () => {
    setSavingOrder(true)
    try {
      // Create array with new order values (1-based)
      const orderData = publishedProjects.map((project, index) => ({
        id: project.id,
        website_order: index + 1
      }))
      
      await httpClient.post(import.meta.env.VITE_API_URL + `/api/projects/website-order`, orderData)
      toast.success("Website order saved successfully")
      handleCloseWebsiteOrderModal()
    } catch (error) {
      console.error("Error saving website order:", error)
      toast.error("Failed to save website order")
    }
    setSavingOrder(false)
  }

  const renderMultiValueField = (values, emptyLabel = 'N/A') => {
    if (!values || values.length === 0) {
      return emptyLabel
    }

    return (
      <div className='labels-container'>
        {values.map((value, index) => (
          <span className='label-item' key={`${value}-${index}`}>{value}</span>
        ))}
      </div>
    )
  }

  const renderProjectStatuses = (project) => {
    const statuses = Array.isArray(project.statuses) && project.statuses.length > 0
      ? project.statuses
      : project.status_id
        ? [project.status_id]
        : []

    if (statuses.length === 0) {
      return null
    }

    return (
      <div className='labels-container project-status-labels'>
        {statuses.map(status => (
          <Label key={status.id} title={status.status_name} labelColor={status.label_color} maxWidth="none" />
        ))}
      </div>
    )
  }

  return (
    <div className='template'>

      <Header>
        {
          createNew ?
            <Button className="ui-button--header-right" variant='primary' size='sm' id="projects__btn-save" ref={saveButton} onClick={handleSaveNewProject}>Save</Button>
            :
            <div className="header__content">
              <div className="header__left">
                <Button 
                  variant='secondary'
                  size='sm'
                  onClick={handleOpenWebsiteOrderModal}
                >
                  Website Order
                </Button>
                <ExportButton
                  data={prepareExportData}
                  filename="projects_export"
                  formats={['excel']}
                  disabled={loadingProjects}
                />
              </div>
              <div className="header__filters" id="products__table-btns">
                <div className="search-bar" id="products__search-bar--table" onClick={handleClearSearchBar}>
                  <input placeholder="Search project..." value={filters.searchWord} onChange={handleSearchWord} />
                  {filters.searchWord == "" ? <SearchIcon /> : <ClearIcon />}
                </div>

                <DropDownFilter
                  idList="projects_filter--status"
                  placeholderText="Filter on status"
                  listArray={statuses}
                  onClickItem={handleOnClickDropdownItem}
                  filters={filters}
                  value={filters.statusFilter?.name || ""}
                  filter={"statusFilter"}
                  setValue={updateFilters}
                />

                <DropDownFilter
                  idList="projects_filter--tag"
                  placeholderText="Filter on tag"
                  listArray={tagsList}
                  onClickItem={handleOnClickDropdownItem}
                  filters={filters}
                  value={filters.tagFilter?.name || ""}
                  filter={"tagFilter"}
                  setValue={updateFilters}
                />
                <div className="hide-archived-filter">
                  <Slider
                    text="Hide archived"
                    defaultState={filters.hideArchived !== false}
                    state={filters.hideArchived !== false}
                    size={1.2}
                    fontSize="0.8rem"
                    onClickFunction={(id, newState) => {
                      if (!loadingProjects) {
                        dispatch(updateFilters({ ...filters, hideArchived: newState, page: 1 }))
                      }
                    }}
                  />
                </div>
              </div>
            </div>
        }
      </Header>

      <MenuBar />

      <div className="template__body" id='body-projects'>
        <button className="floating__button--bottom-right" id="add--btn" onClick={() => setCreateNew(!createNew)} />

        <div className={`body__section ${createNew ? "" : "hide"}`} id='new-project-form'>
          <div className="form__section">
            <div className="input_field">
              <label className="input_field__label" htmlFor="project_placeholder_name">Working name</label>
              <input className="input_field__input_box--text" id="project_placeholder_name" type="text" name="project_placeholder_name" />
            </div>
            <div className="input_field">
              <label className="input_field__label" htmlFor="project_commercial_name">Commercial name</label>
              <input className="input_field__input_box--text" id="project_commercial_name" type="text" name="project_commercial_name" />
            </div>
            <div className="input_field">
              <label className="input_field__label" htmlFor="code_aangifte_van_werken">Code aangifte van werken</label>
              <input className="input_field__input_box--text" id="code_aangifte_van_werken" type="text" name="code_aangifte_van_werken" />
            </div>
          </div>
          <div className="form__section">
            <div className='flex--vert dropdown'>
              <label className="input_field__label" htmlFor="company_number">Project status</label>
              <Dropdown
                index={"project-status"}
                listArray={statuses}
                state={newProjectStatus}
                setState={setNewProjectStatus}
                placeholder={""}
                width={"100%"}
                searchBar={false}
                margin={"0"}
                objDropdownType={"normal"}
              />
            </div>
          </div>
        </div>
        <div className={`body__section ${createNew ? "" : "big"}`} id='all-projects-table'>
          <div className='table'>
            <div className='table__header-4 projects-table-grid'>
              <div className='table__header--field' onClick={handleSetSortFilter}>id</div>
              <div className='table__header--field' onClick={handleSetSortFilter}>Working name</div>
              <div className='table__header--field' onClick={handleSetSortFilter}>Commercial name</div>
              <div className='table__header--field'>Buildings</div>
              <div className='table__header--field' onClick={handleSetSortFilter}>Status</div>
              <div className='table__header--field' onClick={handleSetSortFilter}>Tags</div>
            </div>
            <div className='table__rows_container'>

              {
                loadingProjects ?
                  <LoadingSpinner /> :

                  projects.map((project, index) => {
                    return (
                      <div className={`table__row-4 projects-table-grid ${project.is_archived ? 'archived' : ''}`} key={index} onClick={() => handleOnClickRow(project.id)} data-project-id={project.id}>
                        <div className='table__row--field'>{project.id}</div>
                        <div className='table__row--field'>{project.project_placeholder_name ? project.project_placeholder_name : "N/A"}</div>
                        <div className='table__row--field'>{project.project_commercial_name ? project.project_commercial_name : "TBD"}</div>
                        <div className='table__row--field'>{renderMultiValueField(project.building_names)}</div>
                        <div className='table__row--field'>{renderProjectStatuses(project)}</div>
                        <div className='table__row--field tags'>
                          {project.is_archived && <span className="label-item archived-label">Archived</span>}
                          {renderMultiValueField(project.tags.map(tag => tag.tag_id.tag), '')}
                        </div>
                      </div>
                    )
                  })
              }
            </div>
            <div className="pagination">
              <ArrowBackIosIcon className={`pagination__btn ${filters.page == 1 ? "grayed-out" : ""}`} onClick={handlePagination} data-type="previous" id="prevPage" />
              <ArrowForwardIosIcon className={`pagination__btn ${filters.page == totalPages ? "grayed-out" : ""}`} onClick={handlePagination} data-type="next" id="nextPage" />
            </div>
          </div>
        </div>
      </div>

      {/* Website Order Modal */}
      {websiteOrderModalOpen && (
        <div className="modal-overlay" onClick={handleCloseWebsiteOrderModal}>
          <div className="modal-content website-order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Website Project Order</h3>
              <button className="close-btn" onClick={handleCloseWebsiteOrderModal}>
                <CloseIcon />
              </button>
            </div>
            
            <p className="modal-description">
              Drag and drop projects to change their order on the website. 
              Position 1 appears first.
            </p>
            
            <div className="website-order-list">
              {loadingPublishedProjects ? (
                <LoadingSpinner />
              ) : publishedProjects.length === 0 ? (
                <p className="no-projects">No published projects found</p>
              ) : (
                publishedProjects.map((project, index) => (
                  <div
                    key={project.id}
                    className={`website-order-item ${draggedIndex === index ? 'dragging' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="drag-handle">
                      <RxDragHandleHorizontal />
                    </div>
                    <span className="order-number">{index + 1}</span>
                    <span className="project-name">
                      {project.project_commercial_name || project.project_placeholder_name || `Project #${project.id}`}
                    </span>
                  </div>
                ))
              )}
            </div>
            
            <div className="modal-actions">
              <Button variant='secondary' size='md' onClick={handleCloseWebsiteOrderModal}>
                Cancel
              </Button>
              <Button 
                variant='primary'
                size='md'
                onClick={handleSaveWebsiteOrder}
                disabled={savingOrder || publishedProjects.length === 0}
              >
                {savingOrder ? 'Saving...' : 'Save Order'}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default ProjectsPage
